/**
 * Homeowner plan emails. Browser-safe. No Stripe price ids.
 * CommonJS only. No object spread.
 */

const { PACARION_POSTAL_ADDRESS } = require('./sponsorlessBrand');
const { formatEmailDate } = require('../utils/dates');

const HOMEOWNER_CANCEL_PATH = 'More → Subscription → Cancel plan, in the app or at app.pacarion.com/homeowner/plan';
const HOMEOWNER_PLAN_URL = 'https://app.pacarion.com/homeowner/plan';
const DAY_MS = 24 * 60 * 60 * 1000;

function atMs(now) {
  if (now instanceof Date) return now.getTime();
  return new Date(now || Date.now()).getTime();
}

function addDays(now, days) {
  return new Date(atMs(now) + (days * DAY_MS)).toISOString();
}

function formatRenewalDate(iso) {
  return formatEmailDate(iso);
}

function cardPhrase(last4) {
  const digits = String(last4 || '').replace(/\D/g, '');
  if (digits.length >= 4) return `your card ending ${digits.slice(-4)}`;
  return 'your card on file';
}

function annualRenewalCopy(account, extras) {
  const when = formatRenewalDate(account && account.current_period_end);
  const card = cardPhrase(extras && extras.last4);
  return {
    subject: `Your Pacarion Homeowner plan renews on ${when}`,
    paragraphs: [
      `Your Pacarion Homeowner plan renews automatically on ${when} for $49.00, charged to ${card}. To cancel, go to ${HOMEOWNER_CANCEL_PATH}. Cancel before ${when} and you won't be charged again. If you're charged and change your mind, you can request a full refund within 7 days after the renewal charge. Questions: support@pacarion.com.`,
      `Pacarion LLC, ${PACARION_POSTAL_ADDRESS}.`,
    ],
  };
}

function homeownerAckCopy({ interval, periodEnd, last4, now }) {
  const annual = interval === 'year';
  const chargedOn = formatRenewalDate(now || new Date());
  const nextCharge = formatRenewalDate(periodEnd);
  const refundBy = formatRenewalDate(addDays(now || new Date(), 7));
  const price = annual ? '$49.00 per year' : '$4.99 per month';
  const every = annual ? 'year' : 'month';
  const plan = annual ? 'Annual' : 'Monthly';
  const card = cardPhrase(last4);
  const annualReminder = annual ? ' We\'ll email you 35 and 7 days before each renewal.' : '';
  const annualRefund = annual ? ' Also within 7 days after each renewal charge.' : '';
  return {
    subject: 'Your Pacarion Homeowner plan is active',
    paragraphs: [
      'Thanks for subscribing to the Pacarion Homeowner plan.',
      `Plan: Homeowner ${plan}.`,
      `Price: ${price}, charged today (${chargedOn}) to ${card}.`,
      `Automatic renewal: your plan renews automatically at the same price every ${every} until you cancel. Next charge: ${nextCharge}.${annualReminder}`,
      `How to cancel: ${HOMEOWNER_CANCEL_PATH}. You can cancel online anytime, and you'll keep access through the end of your paid period.`,
      `Refunds: full refund of your first charge if you ask within 7 days (by ${refundBy}).${annualRefund} No prorated refunds.`,
      'Terms: https://app.pacarion.com/terms. Refund Policy: https://app.pacarion.com/refunds. Auto-Renewal Terms: https://app.pacarion.com/trial-terms.',
      `Pacarion LLC, ${PACARION_POSTAL_ADDRESS}. support@pacarion.com.`,
    ],
  };
}

function homeownerCancelCopy(account) {
  const when = formatRenewalDate(account && account.current_period_end);
  return {
    subject: 'Your Pacarion Homeowner plan will not renew',
    paragraphs: [
      when
        ? `Your Pacarion Homeowner plan is canceled. You keep access until ${when}, and it will not renew.`
        : 'Your Pacarion Homeowner plan is canceled. You keep access through the end of the paid period, and it will not renew.',
      'Questions: support@pacarion.com.',
      `Pacarion LLC, ${PACARION_POSTAL_ADDRESS}.`,
    ],
  };
}

module.exports = {
  HOMEOWNER_CANCEL_PATH,
  HOMEOWNER_PLAN_URL,
  annualRenewalCopy,
  homeownerAckCopy,
  homeownerCancelCopy,
};
